import { useEffect, useState, useCallback } from "react";
import { Flex, Text, Icon } from "@chakra-ui/react";
import { CloudUpload, CheckCircle2 } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import { getOutbox, flushOutbox } from "@/lib/offline";
import { colors } from "@/theme/colors";

// Global sync agent: flushes queued submissions on mount, on 'online', and
// whenever the app is reopened/foregrounded (iOS has no Background Sync).
// Shows a pending-count pill so field workers always know what hasn't sent.
export const OutboxSync = () => {
  const { user } = useAuth();
  const [pending, setPending] = useState(0);
  const [justSent, setJustSent] = useState(0);

  const sync = useCallback(async () => {
    if (!user) return;
    setPending((await getOutbox(user.id)).length);
    if (!navigator.onLine) return;
    const { sent, remaining } = await flushOutbox(supabase, user.id);
    setPending(remaining);
    if (sent > 0) {
      setJustSent(sent);
      setTimeout(() => setJustSent(0), 5000);
    }
  }, [user]);

  useEffect(() => {
    sync();
    const onOnline = () => sync();
    const onVisible = () => { if (document.visibilityState === "visible") sync(); };
    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [sync]);

  if (justSent > 0) {
    return (
      <Flex position="fixed" bottom="4" left="50%" transform="translateX(-50%)" zIndex={150}
        bg={colors.resolvedBg} color={colors.resolvedText} px="4" py="2" borderRadius="full"
        align="center" gap="2" boxShadow="0 4px 16px rgba(31,41,55,0.15)" data-testid="outbox-sent-pill">
        <Icon as={CheckCircle2} boxSize="4" />
        <Text fontSize="sm" fontWeight="500">{justSent} queued form{justSent === 1 ? "" : "s"} sent</Text>
      </Flex>
    );
  }
  if (pending === 0) return null;
  return (
    <Flex position="fixed" bottom="4" left="50%" transform="translateX(-50%)" zIndex={150}
      bg="#FBEEDA" color="#8A5106" px="4" py="2" borderRadius="full"
      align="center" gap="2" boxShadow="0 4px 16px rgba(31,41,55,0.15)" data-testid="outbox-pending-pill">
      <Icon as={CloudUpload} boxSize="4" />
      <Text fontSize="sm" fontWeight="500">{pending} form{pending === 1 ? "" : "s"} waiting to sync</Text>
    </Flex>
  );
};
